import React from "react";
import {
  PageMeta,
  BlockHero,
  BlockSection,
  BlocksCatalogTable,
  AllowedSpansLegend,
  BlockText,
  BlockQuote,
  BlockSpec,
  BlockPAOCell,
  BlockMediaPlaceholder,
  BlockDiagramPlaceholder,
  BlockStepsRow,
  BlockDecision,
  BlockDiff,
  RulesGrid,
  DecisionTreeBlock,
  GridRecipeBoard,
  SequenceDemo,
  DiagramStepsDemo,
  LiveCubeDemo,
  RecipeRow,
  RecipeCell,
} from "../projectBlocks";
import {
  projectPageShellClass,
  projectPageBottomClass,
  blockSectionTitleClass,
  blockMonoLabelClass,
  blockSectionSpacingClass,
  previewSectionSubtitleClass,
  previewClosingNoteClass,
} from "../tokens";
import { PROJECT_BLOCKS } from "../projectBlocks/catalog";

const v1Blocks = [
  { num: "01", title: "Summary", note: "lede near top" },
  { num: "02", title: "P/A/O cell" },
  { num: "03", title: "Process", note: "long-form text" },
  { num: "04", title: "Quote" },
  { num: "05", title: "Spec" },
  { num: "06", title: "Image" },
  { num: "07", title: "Image group", note: "triptych / diptych" },
  { num: "08", title: "Video" },
  { num: "09", title: "Loop (gif)", note: "autoplay muted" },
  { num: "10", title: "Diagram", note: "static svg" },
  { num: "11", title: "Steps", note: "numbered row" },
  { num: "12", title: "Carousel ★" },
  { num: "13", title: "Compare" },
];

const v2Blocks = PROJECT_BLOCKS.map((b, idx) => ({
  num: String(idx + 1).padStart(2, "0"),
  title: b.name,
  note: b.summary,
}));

function PreviewSection({ id, kicker, title, subtitle, children }) {
  return (
    <section
      id={id}
      className={`scroll-mt-28 first:pt-0 ${blockSectionSpacingClass}`}
    >
      <p className={blockMonoLabelClass}>{kicker}</p>
      <h2 className={`mt-3 ${blockSectionTitleClass}`}>{title}</h2>
      {subtitle && (
        <p className={previewSectionSubtitleClass}>
          {subtitle}
        </p>
      )}
      <div className="mt-8">{children}</div>
    </section>
  );
}

function Nav() {
  const links = [
    { href: "#catalog", label: "Catalog" },
    { href: "#shapes", label: "Row shapes" },
    { href: "#blocks", label: "Each block" },
    { href: "#interactive", label: "Control bar" },
    { href: "#grid-board", label: "Grid board" },
    { href: "#compose-a", label: "Compose A" },
    { href: "#compose-b", label: "Compose B" },
    { href: "#rules", label: "Rules & tree" },
  ];
  return (
    <nav className="sticky top-[72px] z-10 -mx-3 px-3 py-3 mb-10 bg-white/90 dark:bg-black/90 backdrop-blur-sm">
      <ul className={`flex flex-wrap gap-x-4 gap-y-2 ${blockMonoLabelClass}`}>
        {links.map((l) => (
          <li key={l.href}>
            <a
              href={l.href}
              className="text-content-muted hover:text-black dark:hover:text-white underline-offset-4 hover:underline"
            >
              {l.label}
            </a>
          </li>
        ))}
      </ul>
    </nav>
  );
}

export default function ProjectBlocksPreviewPage() {
  return (
    <div
      className={`min-h-screen bg-white dark:bg-black text-black dark:text-white transition-colors duration-500 pt-24 ${projectPageShellClass} ${projectPageBottomClass}`}
    >
      <PageMeta
        left={["Dev · /project-blocks", "Project page blocks"]}
        right={["Calude · html-spec/Project Pages v2", "React preview"]}
      />

      <BlockHero
        kicker="Project sections — block kit"
        title="Ten blocks, four row shapes,"
        titleAccent="one control pattern."
        lede="This page mirrors Project Pages v2: catalogue every block, show how spans compose on a 12-column grid, and demonstrate the shared control bar for Sequence, Diagram steps, and Live (3D). Use it when building case studies under pages/projects."
      />

      <Nav />

      <PreviewSection
        id="catalog"
        kicker="Reference"
        title="Block catalogue"
        subtitle="Stable IDs for future MDX / JSON content. Spans are hints — mobile stacks to full width."
      >
        <BlocksCatalogTable />
        <BlockDecision label="v1 → v2">
          <p>
            Summary + Process collapsed into <strong>Text</strong>. Loop is a flag on{" "}
            <strong>Image</strong>. Image groups are just two Image blocks (e.g. 6+6). Carousel →{" "}
            <strong>Sequence</strong>; Compare → <strong>Live</strong>.
          </p>
        </BlockDecision>
        <BlockDiff leftTitle="v1 · 13 blocks" rightTitle="v2 · 10 blocks" leftItems={v1Blocks} rightItems={v2Blocks} />
      </PreviewSection>

      <PreviewSection
        id="shapes"
        kicker="Layout"
        title="Four row shapes"
        subtitle="Every row sums to 12. Pick the shape first, then drop blocks. Sequence and Live usually stay at s12."
      >
        <AllowedSpansLegend />
      </PreviewSection>

      <PreviewSection
        id="blocks"
        kicker="Primitives"
        title="Each block — isolated"
        subtitle="Typography and borders follow the primary site tokens (Tailwind content-* / border)."
      >
        <div className="space-y-12">
          <div>
            <p className="font-mono text-meta uppercase text-content-muted mb-3">text</p>
            <BlockText
              heading="How it was made"
              paragraphs={[
                "Optional section heading plus one or more paragraphs — same block for intro copy and long process narrative.",
                "Second paragraph for rhythm; keep max width comfortable (~74ch in production).",
              ]}
            />
          </div>
          <div>
            <p className="font-mono text-meta uppercase text-content-muted mb-3">quote</p>
            <BlockQuote
              quote="The first computational study to treat the sandstone as a living surface rather than a backdrop."
              attribution="Studio jury notes"
            />
          </div>
          <div>
            <p className="font-mono text-meta uppercase text-content-muted mb-3">spec</p>
            <BlockSpec lines={["2024", "Designer · Shareef Jasim", "Grasshopper · Rhino · Civic study"]} />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <BlockPAOCell role="Problem" body="Circulation never touched the heritage cliff edge." />
            <BlockPAOCell role="Approach" body="Parametric mediation between program strips and rock strata." />
            <BlockPAOCell role="Outcome" body="Public route threaded through stone toward the water." />
          </div>
          <div>
            <p className="font-mono text-meta uppercase text-content-muted mb-3">steps</p>
            <BlockStepsRow steps={["Site & brief", "Constraint model", "Massing iterations", "Documentation"]} />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <BlockMediaPlaceholder label="Image · still or loop" spanLabel="s6" aspectClass="aspect-video" />
            <BlockDiagramPlaceholder />
          </div>
          <div>
            <p className="font-mono text-meta uppercase text-content-muted mb-3">video</p>
            <BlockMediaPlaceholder label="Video placeholder · click-to-play in production" spanLabel="s12" aspectClass="aspect-video" />
          </div>
        </div>
      </PreviewSection>

      <PreviewSection
        id="interactive"
        kicker="Pattern"
        title="One control bar · three engines"
        subtitle="Prev / Next / (optional Play) · caption · counter. Swap the stage: slides, SVG steps, or WebGL."
      >
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-10 lg:gap-8">
          <div>
            <p className="font-mono text-meta uppercase text-content-muted mb-4 text-center">
              Sequence · images
            </p>
            <SequenceDemo
              captions={[
                "Initial massing study",
                "Relaxed the rock edge",
                "Sectional integration",
                "Final scheme — community space",
              ]}
            />
          </div>
          <div>
            <p className="font-mono text-meta uppercase text-content-muted mb-4 text-center">
              Sequence · diagram states
            </p>
            <DiagramStepsDemo
              captions={[
                "Step 01 — site enters",
                "Step 02 — rules constrain",
                "Step 03 — form emerges",
                "Step 04 — studio review",
              ]}
            />
          </div>
          <div>
            <p className="font-mono text-meta uppercase text-content-muted mb-4 text-center">
              Live · 3D specimen
            </p>
            <LiveCubeDemo />
          </div>
        </div>
      </PreviewSection>

      <PreviewSection
        id="grid-board"
        kicker="Teaching aid"
        title="Grid recipe board"
        subtitle="Same pedagogical layout as html-spec/Project Pages v2.html — hairline borders, no gutters between cells."
      >
        <GridRecipeBoard />
      </PreviewSection>

      <PreviewSection
        id="compose-a"
        kicker="Together"
        title="Composed section A — Civic cliff study"
        subtitle="Opening narrative → P/A/O row → hero media → text + diagram pair → sequence strip."
      >
        <div className="border border-border dark:border-neutral-800 bg-white dark:bg-black overflow-hidden">
          <RecipeRow label="Intro band">
            <RecipeCell span={12}>
              <BlockText
                paragraphs={[
                  "A linear sequence you might ship as one scroll chapter: establish context, frame problem/approach/outcome, land a hero visual, then explain mechanics beside an interactive diagram.",
                ]}
              />
            </RecipeCell>
          </RecipeRow>
          <RecipeRow label="Framing">
            <RecipeCell span={4}>
              <BlockPAOCell role="Problem" body="Harbour access blocked by heritage berm." />
            </RecipeCell>
            <RecipeCell span={4}>
              <BlockPAOCell role="Approach" body="Single continuous surface negotiating slope + tide." />
            </RecipeCell>
            <RecipeCell span={4}>
              <BlockPAOCell role="Outcome" body="Dual entrance: civic plaza + coastal path." />
            </RecipeCell>
          </RecipeRow>
          <RecipeRow>
            <RecipeCell span={12} className="!p-0">
              <BlockMediaPlaceholder label="Hero · cover image or loop" spanLabel="s12" aspectClass="aspect-[21/9]" />
            </RecipeCell>
          </RecipeRow>
          <RecipeRow label="Process evidence">
            <RecipeCell span={6}>
              <BlockText
                heading="Parametric workflow"
                paragraphs={[
                  "Weekly iterations encoded setback curves from coastal zoning; solver favoured shortest walking gradient under wind exposure caps.",
                ]}
              />
            </RecipeCell>
            <RecipeCell span={6} className="!p-0">
              <BlockDiagramPlaceholder />
            </RecipeCell>
          </RecipeRow>
          <RecipeRow label="Iteration arc">
            <RecipeCell span={12} className="items-center py-6">
              <SequenceDemo captions={["Study A — datum only", "Study B — terraces", "Study C — carved void", "Study D — voted scheme"]} />
            </RecipeCell>
          </RecipeRow>
        </div>
      </PreviewSection>

      <PreviewSection
        id="compose-b"
        kicker="Together"
        title="Composed section B — Tool + outcome"
        subtitle="Quote + diptych → live viewer + caption → metadata strip."
      >
        <div className="border border-border dark:border-neutral-800 bg-white dark:bg-black overflow-hidden">
          <RecipeRow>
            <RecipeCell span={6}>
              <BlockQuote quote="Finally a viewport that doesn't fight the Grasshopper graph." attribution="Internal reviewer" />
            </RecipeCell>
            <RecipeCell span={6} className="!p-0">
              <BlockMediaPlaceholder label="UI capture · s6" spanLabel="s6" aspectClass="aspect-square" />
            </RecipeCell>
          </RecipeRow>
          <RecipeRow>
            <RecipeCell span={8} className="items-center py-4">
              <LiveCubeDemo />
            </RecipeCell>
            <RecipeCell span={4}>
              <BlockText
                heading="Live block"
                paragraphs={[
                  "Embed GLTF viewer or comparison slider. Keep companion copy narrow — readers skim while manipulating.",
                ]}
              />
              <BlockSpec lines={["Web · Three.js", "HDR probe · custom orbit"]} className="mt-4" />
            </RecipeCell>
          </RecipeRow>
          <RecipeRow>
            <RecipeCell span={12}>
              <BlockStepsRow steps={["Parse CAD anchors", "Solve facade lattice", "Emit fabrication sheets"]} />
            </RecipeCell>
          </RecipeRow>
        </div>
      </PreviewSection>

      <PreviewSection
        id="rules"
        kicker="Motion"
        title="Auto vs manual · picking a block"
        subtitle="Operational guidance copied from the HTML spec, tightened for implementation."
      >
        <RulesGrid />
        <BlockSection answerLabel="Decision tree" title="Ask in order" titleAccent="">
          <DecisionTreeBlock />
          <p className={previewClosingNoteClass}>
            Ten blocks · four row shapes · one control bar. If you reach for an eleventh primitive, ask whether it is a new Sequence or Live engine first — usually it is.
          </p>
        </BlockSection>
      </PreviewSection>

      <footer
        className={`mt-16 pt-8 flex flex-wrap justify-between gap-6 ${blockMonoLabelClass}`}
      >
        <span>Roboto · ui-monospace</span>
        <span>Spec: Calude design portfolio / html-spec / Project Pages v2.html</span>
        <span>10 blocks · 4 shapes · 1 bar</span>
      </footer>
    </div>
  );
}
